import { z } from 'zod';

const MemberInput = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string(),
  email: z.string(),
});

const AddressInput = z.object({
  addressLine1: z.string(),
  city: z.string(),
  postalCode: z.string(),
  country: z.string().optional(),
  region: z.string().optional(),
});

const Solution = z
  .object({
    type: z.literal('Name'),
    id: z.string(),
    name: z.string(),
    price: z.string().nullable(),
    quantity: z.number().int().nullable(),
    currency: z.string().nullable(),
    solutionType: z.enum(['1P', '3P', 'SA']),
    publisherName: z.string(),
    closingDateTime: z.string().nullable(),
  })
  .partial()
  .passthrough();

const CustomerMarketplaceIntentAttribute = z.object({
  id: z.enum(['Yes', 'No', 'HaveNotDecided']),
  type: z.literal('CustomerMarketplaceIntent'),
});
const SolutionAttribute = z.object({
  id: z.string(),
  type: z.enum(['SolutionArea', 'SolutionPlay']),
});

const Attribute = z.discriminatedUnion('type', [
  CustomerMarketplaceIntentAttribute,
  SolutionAttribute,
]);

const Requirements = z
  .object({
    solutions: z.array(Solution).nullable(),
    additionalRequirements: z.object({
      attributes: z.array(Attribute.optional()),
    }),
  })
  .partial()
  .passthrough();

const Details = z
  .object({
    notes: z.string().optional(),
    dealValue: z.number(),
    currency: z.string(),
    closingDateTime: z.string(),
    requirements: Requirements,
  })
  .partial()
  .passthrough();

const CustomerProfile = z
  .object({
    name: z.string(),
    isMatchingComplete: z.boolean(),
    isMaccEligible: z.boolean(),
    ids: z.array(
      z
        .object({ id: z.string(), profileType: z.enum(['External', 'duns']) })
        .strict(),
    ),
    address: AddressInput,
    team: z.array(MemberInput),
  })
  .partial();

const ConsentInput = z
  .object({
    consentToShareReferralWithMicrosoftSellers: z.boolean(),
    consentToShareInfoWithOthers: z.boolean(),
    consentToContact: z.boolean(),
    consentToMicrosoftToContactSpecificPartners: z.boolean(),
  })
  .partial()
  .passthrough();

const ReferralDirection = z.enum(['Incoming', 'Outgoing']);

/** TODO: remove any commented out data that we do not need. */
export const Referral = z
  .object({
    id: z.nullable(z.string()),
    // engagementId: z.string(),
    name: z.string(),
    externalReferenceId: z.string(),
    createdDateTime: z.string(),
    updatedDateTime: z.string(),
    // expirationDateTime: z.string(),
    // acceptedDateTime: z.string(),
    status: z.nullable(z.enum(['New', 'Active', 'Closed'])),
    substatus: z.nullable(
      z.enum([
        'Pending',
        'Received',
        'Accepted',
        'Won',
        'Lost',
        'Declined',
        'Expired',
        'Error',
      ]),
    ),
    statusReason: z.string(),
    // type: z.enum(['Shared', 'Independent']),
    // tags: z.array(z.string()),
    // qualification: z.enum(['Direct', 'MarketingQualified', 'SalesQualified']),
    customerProfile: z.nullable(CustomerProfile),
    consent: z.nullable(ConsentInput),
    details: z.nullable(Details),
    team: z.nullable(z.array(MemberInput)),
    // inviteContext: InviteContextInput,
    // eTag: z.string(),
    // target: z.array(Target),
    mpnId: z.nullable(z.string()),
    organizationId: z.string(),
    // organizationName: z.string(),
    lastModifiedVia: z.string(),
    // lastRunId: z.string(),
    // referralSource: z.string(),
    // favorite: z.boolean(),
    campaignId: z.string(),
    // salesStage: z.string(),
    // quality: z.string(),
    // isSpam: z.boolean(),
    direction: z.nullable(ReferralDirection),
    // referralProgram: z.string(),
    // dealSensitivity: z.string(),
    // createdVia: z.string(),
    // closedDateTime: z.string(),
    // callToAction: z.string(),
    // registrationStatus: z.string(),
    // isAutoRegistrationEnabled: z.string(),
    // links: z.record(Link),
    // trackingInfo: TrackingInfo,
    // exception: z.union([
    //   z.object({}).partial().strict().passthrough(),
    //   z.string(),
    // ]),
  })
  .partial();
// .passthrough();

const DealType = z.enum(['coSell', 'private', 'p2p', 'partnerLed']);

const Metadata = z
  .object({
    tackleId: z.string(),
    companyName: z.string(),
    createdDate: z.string(),
    lastModifiedDate: z.string(),
    crmId: z.string(),
    /** TODO: check if this is a variable anymore */
    pendingRequestIds: z.nullable(z.array(z.string())),
    hasCloudErrors: z.boolean().optional(),
    pendingRequestItemIds: z.array(z.string()),
    classification: z.enum([
      'ip',
      'profileMarketplaceLead',
      'offerMarketplaceLead',
      'coMarketingQualifiedLead',
      'jointPlanningQualifiedLead',
      'qualifiedLead',
      'solutionAssessment',
      'oemiot',
      'services',
      'unknown',
    ]),

    /** dealType only applies to 'ip', 'services' */
    dealType: DealType,
  })
  .partial()
  .passthrough();

export const getReferral_Response = Referral.extend({
  metadata: Metadata,
}).transform((values) => ({
  ...values,
  isCreatedInCloud: !!values.createdDateTime?.includes('T'),
}));

/** --- events --------------------- */

const EventActivityDetails = z
  .object({
    displayText: z.string(),
    id: z.string(),
    type: z.string(),
    givenName: z.string(),
    familyName: z.string(),
    pictureUrl: z.string(),
    email: z.string(),
  })
  .partial()
  .passthrough();

const BaseEvent = z.object({
  eventId: z.string(),
  timestamp: z.string(),
  activityDetails: EventActivityDetails,
});

const GenericEvent = BaseEvent.extend({
  operation: z.enum(['create', 'internalUpdateEvent', 'cloudUpdateEvent']),
});

const CloudRequest = z
  .object({
    requestId: z.string(),
    operation: z.string(),
    timestamp: z.string(),
    request: z.object({}),
  })
  .partial()
  .passthrough();
const CloudResponse = z
  .object({
    requestId: z.string(),
    operation: z.string(),
    timestamp: z.string(),
    rawResponse: z.object({}),
    success: z.boolean(),
    errorType: z.nullable(z.string()),
    errorDetails: z
      .object({
        code: z.string(),
        message: z.string(),
        target: z.string(),
      })
      .partial()
      .passthrough(),
  })
  .partial()
  .passthrough();

const CreateIPReferralEvent = BaseEvent.extend({
  operation: z.literal('createIPReferral'),
  cloudRequests: z.array(CloudRequest),
  cloudResponses: z.array(CloudResponse),
  rawRequest: z.object({}),
})
  .partial()
  .passthrough();

export const getReferralEvents_Response = z.object({
  events: z.array(
    z.discriminatedUnion('operation', [CreateIPReferralEvent, GenericEvent]),
  ),
});

/** referral list summary ------------------ */
/** TODO: remove items not needed */
const ReferralSummary = z
  .object({
    type: z.string(),
    tackleId: z.string(),
    sourceAccount: z.string(),
    created: z.string(),
    lastModified: z.string(),
    cloudProvider: z.literal('msft'),
    cloudProviderId: z.string().optional(),
    companyName: z.string(),
    dealType: DealType,
    source: ReferralDirection,
    crmId: z.string(),
    targetCloseDate: z.string(),
    title: z.string(),
    classification: z.string(),
    isMaccEligible: z.boolean(),
    cloudLastModified: z.string(),
    cloudStatus: z.string(),
    cloudSubStatus: z.string(),
    cloudCreated: z.string(),
    engagementId: z.string(),
    dealValue: z.number(),
    hasCloudErrors: z.boolean(),
  })
  .partial()
  .passthrough();

export const getReferralListByCrmId_Response = z
  .object({
    referrals: z.array(ReferralSummary),
    total: z.number().int(),
    currentPageCount: z.number().int(),
    nextIndex: z.number().int().optional(),
    isNextPage: z.boolean(),
  })
  .partial()
  .passthrough();
